// ProvenanceTimeline.tsx — a date-ordered history for ONE item: acquired, condition notes,
// any event the user logs. Local-only (store.ts → Rust IPC → SQLite); never sent to the AI.
import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { store, type ProvenanceEvent, formatAttr } from "./store";

const KIND_LABEL: Record<string, string> = {
  acquired: "Acquired", condition: "Condition change", restored: "Restored",
  appraised: "Appraised", sold: "Sold", note: "Note",
};
const KINDS = Object.keys(KIND_LABEL);

export function ProvenanceTimeline({ itemId }: { itemId: string }) {
  const [events, setEvents] = useState<ProvenanceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [kind, setKind] = useState("note");
  const [occurredOn, setOccurredOn] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setEvents(await store.listProvenanceEvents(itemId)); }
    finally { setLoading(false); }
  }, [itemId]);
  useEffect(() => { void reload(); }, [reload]);

  async function save() {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await store.createProvenanceEvent({ item_id: itemId, occurred_on: occurredOn || undefined, kind, text: text.trim() });
      setText(""); setOccurredOn(""); setKind("note"); setAdding(false);
      await reload();
    } finally { setSaving(false); }
  }

  async function remove(id: string) {
    await store.deleteProvenanceEvent(id);
    await reload();
  }

  if (loading) return null;

  return (
    <div className="timeline-wrap">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
        <div className="stat-lbl">History</div>
        <button type="button" className="btn btn-sm btn-pill" onClick={() => setAdding((v) => !v)}>
          <Plus size={13} /> Add event
        </button>
      </div>

      {adding && (
        <div className="card" style={{ padding: "0.7rem 0.85rem", marginBottom: "0.6rem" }}>
          <div className="row" style={{ gap: "0.4rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
            {KINDS.map((k) => (
              <button type="button" key={k} className={`chip${kind === k ? " selected" : ""}`} onClick={() => setKind(k)}>
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
          <input type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} style={{ marginBottom: "0.5rem" }} />
          <textarea rows={2} placeholder="What happened?" value={text} onChange={(e) => setText(e.target.value)} style={{ resize: "vertical" }} />
          <div className="row" style={{ justifyContent: "flex-end", marginTop: "0.5rem", gap: "0.4rem" }}>
            <button type="button" className="btn btn-sm btn-pill" onClick={() => setAdding(false)} disabled={saving}>Cancel</button>
            <button type="button" className="btn btn-sm btn-primary btn-pill" onClick={save} disabled={saving || !text.trim()}>
              {saving ? "Saving…" : "Add"}
            </button>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>No history logged yet.</div>
      ) : (
        <div className="timeline">
          {events.map((ev) => (
            <div key={ev.id} className="timeline-row">
              <div className="timeline-dot" />
              <div className="timeline-body">
                <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem" }}>
                  <span style={{ fontWeight: 600, fontSize: "var(--fs-xs)" }}>{KIND_LABEL[ev.kind] ?? formatAttr(ev.kind)}</span>
                  <span className="row" style={{ gap: "0.4rem" }}>
                    {ev.occurred_on && <span className="muted tnum" style={{ fontSize: "var(--fs-xxs)" }}>{ev.occurred_on}</span>}
                    <button type="button" className="tile-del" style={{ width: 22, height: 22 }} aria-label="Delete event" onClick={() => remove(ev.id)}>
                      <Trash2 size={12} />
                    </button>
                  </span>
                </div>
                <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2, whiteSpace: "pre-wrap" }}>{ev.text}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
